import { Grid, Link, Skeleton, Typography } from "@mui/material";

import {
  FairPaySurveyPartnership,
  FairPaySurveyResponse,
} from "features/FairPaySurvey/schemas";
import { trackEvent } from "utils/tracking";

import FullReportCTA from "./FullReportCTA";
import FullReportInProgress from "./FullReportInProgress";
import Opportunities from "./Opportunities";
import PitchNext from "./PitchNext";
import SingleBrandBreakdown from "./SingleBrandBreakdown";

const styles = {
  skeletonWrapper: {
    width: "100%",
  },
  caption: {
    width: "100%",
    textAlign: "center",
    my: 1,
    fontSize: "16px",
    opacity: "60%",
  },
  skeleton: {
    borderRadius: "20px",
  },
  chip: {
    py: 1,
    px: 2,
    mb: 1,
    fontWeight: 700,
    fontSize: "18px",
    color: "#151F16",
    borderRadius: "20px",
    border: `1px solid rgba(21, 31, 22, 0.2)`,
  },
  heading: {
    textAlign: "center",
    maxWidth: "70%",
    fontWeight: 600,
  },
  description: {
    textAlign: "center",
    maxWidth: "80%",
    fontSize: "16px",
  },
};

export default function BrandBreakdown({
  surveyResponse,
  surveyPartnerships,
  loadingPartnerships,
}: {
  surveyResponse: FairPaySurveyResponse | null;
  surveyPartnerships: FairPaySurveyPartnership[] | null;
  loadingPartnerships: boolean;
}) {
  if (surveyResponse === null) {
    return null;
  }

  if (!surveyResponse?.integrationEmail) {
    return <FullReportCTA survey={surveyResponse} />;
  }

  if (loadingPartnerships) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={250}
            sx={styles.skeleton}
          />
        </Grid>
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={250}
            sx={styles.skeleton}
          />
        </Grid>
      </Grid>
    );
  }

  if (!surveyPartnerships || surveyPartnerships.length === 0) {
    if (!surveyResponse?.partnershipsSearchCompletedAt) {
      return <FullReportInProgress />;
    } else {
      return null;
    }
  }

  return (
    <>
      <Grid item xs={12} justifyContent="center" container sx={{ mt: 4 }}>
        <Typography component="div" sx={styles.chip}>
          A snapshot of your brand deal history
        </Typography>
        <Typography sx={styles.caption}>
          For each brand you've worked with
        </Typography>
      </Grid>
      {surveyPartnerships.map((surveyPartnership) => (
        <SingleBrandBreakdown
          surveyPartnership={surveyPartnership}
          key={surveyPartnership.id}
        />
      ))}
      <Grid item xs={12} justifyContent="center" container>
        <Typography variant="h4" sx={[styles.heading, { mt: 3 }]} paragraph>
          💰⚡ Want access to real-time pay data?
        </Typography>
      </Grid>
      <Grid item xs={12} justifyContent="center" container>
        <Typography sx={styles.description} paragraph>
          <em>
            <Link
              href="https://www.onbento.com/va?s=survey"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("Fair Pay - Bento VA Link Clicked", {
                  "Survey UUID": surveyResponse.surveyUuid,
                });
              }}
            >
              Bento's AI Virtual Assistant subscribers
            </Link>{" "}
            get real-time access. Forward a brand convo to Bento's AI and see
            what they really pay - then negotiate with confidence.
          </em>
        </Typography>
      </Grid>
      <Opportunities />
      <PitchNext surveyResponse={surveyResponse} />
    </>
  );
}
